import { Link } from 'react-router';
import { Button, EmptyState, Icon } from '@efira/ui';

import { Resizer } from './Resizer';
import { Gifs } from './right/Gifs';
import { Music } from './right/Music';
import { Pads } from './right/Pads';
import { useMediaTab } from './media';
import { Chat } from './right/Chat';
import { Gifters } from './right/Gifters';
import { StatStrip } from './right/StatStrip';
import { useConsoleSession } from './session';

/**
 * Правая колонка: пады, счётчики, чат, гифтеры (карта экранов, раздел 6).
 *
 * Внутри колонки - ТРЕТЬЯ тянущаяся граница: нижняя кромка области падов.
 * Ленты чата и гифтеров прокручиваются КАЖДАЯ ОТДЕЛЬНО, а не вместе с
 * колонкой: у эталона это три независимые области, и в эфире это важнее, чем
 * кажется - прокрутка чата не должна утаскивать счётчики за край.
 */
export interface RightColumnProps {
  /** Доля высоты колонки под областью падов. */
  pads: number;
  onPadsChange: (fraction: number) => void;
  /** Высота колонки в пикселях: опора для доли при перетаскивании. */
  height: number;
  /** Смещение верха колонки от верха окна. */
  top: number;
}

export function RightColumn({ pads, onPadsChange, height, top }: RightColumnProps) {
  const session = useConsoleSession();
  const { tab } = useMediaTab();

  return (
    <div data-right="column" className="relative flex h-full min-h-0 flex-col">
      {/* Раздел медиа выбирается в верхней полосе, показывается здесь. */}
      <div className="min-h-0 shrink-0" style={{ height: `${pads * 100}%` }}>
        {tab === 'sounds' ? <Pads /> : tab === 'gif' ? <Gifs /> : <Music />}
      </div>

      <Resizer
        orientation="horizontal"
        value={pads}
        onChange={onPadsChange}
        label="Высота области падов"
        extent={height}
        origin={top}
        className="left-0 right-0"
        style={{ top: `${pads * 100}%` }}
      />

      <div className="flex min-h-0 flex-1 flex-col">
        {/*
          ПОКА ЭФИРА НЕТ - ОДНО ПУСТОЕ СОСТОЯНИЕ НА КОЛОНКУ.

          Находка аудита единицы 16 (11): здесь стояли три заглушки
          подряд, и две из них - чат и гифтеры - говорили одно и то же
          слово в слово: «Эфира нет. Выберите игру». Повторённое дважды
          сообщение не становится понятнее, оно становится шумом.

          Без сессии ни ленты, ни гифтеров не существует, поэтому и
          секций для них здесь нет: одно состояние, один заголовок, одно
          действие. Появилась игра - появляются обе секции со своими
          заглушками, и они уже РАЗНЫЕ («Зрители пока молчат» против
          «Подарков пока нет»).
        */}
        {session.game === null ? (
          <div data-right="live-idle" className="flex min-h-0 flex-1 items-center justify-center">
            <EmptyState
              id="live-empty"
              icon="chat"
              title="Эфира нет"
              action={
                <Button asChild variant="primary" size="s">
                  <Link to="/app/games" data-right="live-empty-cta">
                    Выбрать игру
                  </Link>
                </Button>
              }
              hint={
                <Button asChild variant="ghost" size="s" className="text-foreground/80">
                  <a
                    href="https://efira.live/docs"
                    target="_blank"
                    rel="noreferrer"
                    data-right="live-empty-hint"
                  >
                    Как проходит первый эфир
                    <Icon name="open" size="sm" />
                  </a>
                </Button>
              }
            >
              Выберите игру — и всё, что делают зрители, появится здесь: сообщения, подарки и
              счётчики эфира.
            </EmptyState>
          </div>
        ) : (
          <>
            {/* Полоса счётчиков - только при активной сессии: шесть нулей без
                эфира ничего не сообщают и занимают место, нужное ленте. */}
            <StatStrip />

            <Chat />
            <Gifters />
          </>
        )}
      </div>
    </div>
  );
}
